<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // CASSIOPEIA
    import {
        AuctionItem,
        BnplFeesCalculator,
        Card,
        CHANNELS,
        currency,
        Heading,
        integer,
        OutputChannels,
        OutputMarkets,
        OutputShipping,
        OutputUser,
        OutputVertical,
        SESSION_ORGANIZATION,
        UserItem,
    } from '@smatch/cassiopeia-web-components';

    // TYPES
    import type { Bid, Auction } from 'src/app/types';

    /* TYPES ----------------------------------------------------------------------------------- */

    type Channels = NonNullable<Auction['blacklist']>['channelCodes'];

    /* PROPS ----------------------------------------------------------------------------------- */

    export let bid: Bid;
    export let hasShipping: boolean | undefined;
    export let hasServices: boolean | undefined;

    /* INTERNALS  ------------------------------------------------------------------------------ */

    const getWhitelistedChannels = (channelCodes: Channels = []): Channels => CHANNELS
        .map(({ key }) => key)
        .filter(code => !channelCodes.includes(code));

    let fee: number;

    $: ({ currencyCode, selection, financeOptionCode: financeCode, addressKey, logisticalServices } = bid.buyer);

</script>

<div class = 's8-aside'>

    <div class = 's8-header'>


        <AuctionItem key = { bid.auctionKey } let:auction>

            <!-- TODO: Fix slot types -->
            <!-- eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access -->

            <Heading size = 'sm'>Deal Summary</Heading>

            <ul>
                <li>
                    <span>Name</span>
                    <strong>{ auction.name }</strong>
                </li>
                <li>
                    <span>Industry</span>
                    <span>
                        <OutputVertical code = { auction.verticalCode } />
                    </span>
                </li>
                {#if Array.isArray(auction.shipping?.options) && auction.shipping.options.length === 1 }
                    <li>
                        <span>Shipping</span>
                        <span>
                            <OutputShipping option = { auction.shipping.options[0] } />
                        </span>
                    </li>
                {/if}
                {#if Array.isArray(auction.blacklist?.channelCodes) && auction.blacklist.channelCodes.length > 0}
                    <li>
                        <span>Preferred Channels</span>
                        <OutputChannels codes = { getWhitelistedChannels(auction.blacklist?.channelCodes) } />
                    </li>
                {/if}
                {#if Array.isArray(auction.blacklist?.marketCodes) && auction.blacklist.marketCodes.length > 0}
                    <li class = 's8-markets'>
                        Excluded Markets<br />
                        <OutputMarkets maxItems = { 5 } keys = { auction.blacklist?.marketCodes } />
                    </li>
                {/if}
                <li>
                    <span>Items</span>
                    <span>
                        { integer(bid.buyer.selection?.total?.quantity.items ?? 0, false) }
                    </span>
                </li>
            </ul>

            <!-- eslint-enable -->

        </AuctionItem>

    </div>

    <div class = 's8-header-item'>


        <ul>
            {#if selection?.total?.offer !== undefined}
                <li>
                    <span>Your Offer</span>
                    <span>{ currency(selection.total.offer, currencyCode, false) }</span>
                </li>
            {/if}
            {#if financeCode !== undefined
                && financeCode !== 'no_finance'
                && typeof selection?.total?.offer === 'number'}
                <li>
                    <span>Financing Fees</span>
                    <span>
                        <BnplFeesCalculator
                            bind:fee = { fee }
                            currencyCode = { currencyCode ?? 'EUR' }
                            financeOptionCode = { financeCode }
                            value = { selection.total.offer }
                        />
                    </span>
                </li>
            {/if}
            {#if addressKey !== undefined || hasShipping}
                <li>
                    <span>Shipping Fees</span>
                    <span>We will be in touch</span>
                </li>
            {/if}
            {#if logisticalServices !== undefined || hasServices}
                <li>
                    <span>Service Fees</span>
                    <span>We will be in touch</span>
                </li>
            {/if}
            <li class = 's8-total'>
                <span>Total</span>
                <strong>{ currency((selection?.total?.offer ?? 0) + fee, currencyCode, false) }</strong>
            </li>
            <li class = 's8-smallprint'>
                Price is non-binding (availabilities are subject to change prior to invoicing).
            </li>
        </ul>

        <Card>
            <UserItem key = { $SESSION_ORGANIZATION?.managerKey } let:user>
                <OutputUser { user } />
            </UserItem>
        </Card>

    </div>

</div>

<style lang="postcss">

    div.s8-aside {
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-between;
        row-gap: 30px;

        div.s8-header {
            display: flex;
            flex-direction: column;

        }

        ul {
            line-height: 1.6;

            li {
                display: flex;
                justify-content: space-between;

                > strong:last-child,
                > span:last-child {
                    text-align: right;
                }

                &.s8-total {
                    margin-top: 5px;
                    padding: 5px 0px;
                    border-color: rgba(39,39,17,0.1);
                    border-style: solid;
                    border-width: 1px 0px;
                    font-size: 14px;
                }

                &.s8-smallprint {
                    color: rgba(39,39,17,0.4);
                    font-size: 10px;
                    margin-top: 10px;
                    text-align: center;
                }

                &.s8-markets {
                    margin-top: 10px;
                    display: block;
                }
            }
        }

        :global(div.s8-card) {
            margin-bottom: 0px !important;
            padding: 15px !important;
            margin-top: 20px;
        }

        :global(span.s8-channel) {
            margin-right: 0px !important;
        }

        :global(ul.s8-chips) {
            margin-top: 2px;
        }
    }

</style>